// src/pages/Auditorias/Configuracoes/BlocoPrompt.js
import React, { useState } from 'react';
import { verificarVariaveis } from './variaveis';

const ESFORCOS = [
  { valor: 'low', rotulo: 'Baixo' },
  { valor: 'medium', rotulo: 'Médio' },
  { valor: 'high', rotulo: 'Alto' },
];

/**
 * Editor de uma origem: modelo, pensamento e o par SYSTEM/USER.
 *
 * O modelo e o pensamento aparecem **uma vez por origem**, não por linha: o
 * `_carregar_prompt` do Python faz `config["modelo"] or linha.modelo` com o
 * SYSTEM à cabeça, logo só o valor da linha SYSTEM conta. Dois selectores
 * seriam um deles a não fazer nada. Ao gravar, o servidor escreve o mesmo valor
 * nas duas linhas.
 */
const BlocoPrompt = ({
  definicao, rascunho, alterado, aGravar, onEditar, onGuardar, onRepor, onCopiar, modelos,
}) => {
  const [estado, setEstado] = useState(null);

  const { desconhecidas, porUsar } = verificarVariaveis(
    rascunho.texto_system, rascunho.texto_user, definicao.variaveis
  );

  const vazio = !rascunho.texto_system.trim() || !rascunho.texto_user.trim();

  const gravar = async () => {
    setEstado(null);
    try {
      const mensagem = await onGuardar();
      setEstado({ tipo: 'ok', texto: mensagem || 'Prompt gravado.' });
    } catch (e) {
      setEstado({
        tipo: 'erro',
        texto: e.response?.data?.message || 'Erro ao gravar o prompt',
      });
    }
  };

  return (
    <div className="aud-cfg-bloco">
      <p className="aud-cfg-descricao">{definicao.descricao}</p>

      {!definicao.completo && (
        <p className="aud-erro">
          Esta origem não tem as duas linhas (SYSTEM e USER) em
          {' '}<span className="aud-mono">infinity.config_prompt</span>. Gravar não as cria —
          é preciso correr a migração em falta.
        </p>
      )}

      {definicao.divergente && (
        <p className="aud-aviso">
          As linhas SYSTEM e USER têm modelo ou pensamento diferentes na base de dados.
          O motor usa o da linha SYSTEM, que é o que aparece aqui; gravar alinha as duas.
        </p>
      )}

      <div className="aud-cfg-controlos">
        <label className="aud-cfg-campo">
          <span className="aud-cfg-rotulo">Modelo</span>
          <select
            value={rascunho.modelo}
            onChange={(e) => onEditar('modelo', e.target.value)}
          >
            {/* O modelo gravado pode ter saído de infinity.modelos entretanto;
                mantê-lo na lista evita que a caixa apareça vazia e se grave
                outro por acidente */}
            {rascunho.modelo && !modelos.some((m) => m.id === rascunho.modelo) && (
              <option value={rascunho.modelo}>{rascunho.modelo} (fora da lista)</option>
            )}
            {modelos.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name ? `${m.name} — ${m.id}` : m.id}
              </option>
            ))}
          </select>
        </label>

        <label className="aud-cfg-campo">
          <span className="aud-cfg-rotulo">Pensamento</span>
          <select
            value={rascunho.reasoning_effort}
            onChange={(e) => onEditar('reasoning_effort', e.target.value)}
          >
            {ESFORCOS.map((e) => (
              <option key={e.valor} value={e.valor}>{e.rotulo}</option>
            ))}
          </select>
        </label>

        <p className="aud-cfg-nota-controlos">
          Só a série o* e o GPT-5 ou superior usam o pensamento; nos outros modelos
          é ignorado.
        </p>
      </div>

      <div className="aud-cfg-variaveis">
        <span className="aud-cfg-rotulo">Variáveis disponíveis</span>
        <div className="aud-cfg-chips">
          {definicao.variaveis.map((v) => (
            <code
              key={v}
              className={`aud-cfg-chip ${porUsar.includes(v) ? 'aud-cfg-chip-ausente' : ''}`}
            >
              %{v}%
            </code>
          ))}
        </div>
      </div>

      {desconhecidas.length > 0 && (
        <p className="aud-erro">
          O motor não fornece {desconhecidas.map((v) => `%${v}%`).join(', ')} nesta
          origem. Estas vão para o modelo tal como estão escritas, sem serem substituídas.
        </p>
      )}

      {porUsar.length > 0 && (
        <p className="aud-aviso">
          Não está a usar {porUsar.map((v) => `%${v}%`).join(', ')} — esse conteúdo
          não chega ao modelo.
        </p>
      )}

      <Editor
        etiqueta="SYSTEM"
        ajuda="Instrução de sistema — as regras de como avaliar."
        valor={rascunho.texto_system}
        onMudar={(v) => onEditar('texto_system', v)}
        onCopiar={() => onCopiar(rascunho.texto_system, `${definicao.origem} · SYSTEM`)}
      />

      <Editor
        etiqueta="USER"
        ajuda="Mensagem do utilizador — normalmente só o transporte das variáveis."
        valor={rascunho.texto_user}
        onMudar={(v) => onEditar('texto_user', v)}
        onCopiar={() => onCopiar(rascunho.texto_user, `${definicao.origem} · USER`)}
      />

      {estado && (
        <p className={estado.tipo === 'ok' ? 'aud-aviso' : 'aud-erro'}>{estado.texto}</p>
      )}

      <div className="aud-cfg-accoes">
        <button
          type="button"
          className="aud-btn-primario"
          onClick={gravar}
          disabled={!alterado || vazio || aGravar}
        >
          {aGravar ? 'A GRAVAR…' : 'GRAVAR'}
        </button>
        <button
          type="button"
          className="aud-btn-secundario"
          onClick={() => { setEstado(null); onRepor(); }}
          disabled={!alterado || aGravar}
        >
          REPOR
        </button>
        {vazio && (
          <span className="aud-sub aud-sub-alerta">
            Os dois textos são obrigatórios.
          </span>
        )}
        {!vazio && alterado && (
          <span className="aud-sub aud-sub-alerta">Alterações por gravar.</span>
        )}
      </div>
    </div>
  );
};

/** Uma caixa de texto com etiqueta, contagem de caracteres e botão de copiar. */
const Editor = ({ etiqueta, ajuda, valor, onMudar, onCopiar }) => (
  <div className="aud-cfg-editor">
    <div className="aud-cfg-editor-cabeca">
      <span className="aud-cfg-etiqueta">{etiqueta}</span>
      <span className="aud-sub">{ajuda}</span>
      <span className="aud-cfg-contagem">{valor.length.toLocaleString('pt-PT')} caracteres</span>
      <button type="button" className="aud-link" onClick={onCopiar}>copiar</button>
    </div>
    <textarea
      className="aud-cfg-textarea"
      value={valor}
      spellCheck={false}
      onChange={(e) => onMudar(e.target.value)}
    />
  </div>
);

export default BlocoPrompt;
